import './Profile.scss';
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import CountryPicker from "../../components/CountryPicker/CountryPicker";
import AuthContext from "../../context/AuthContext";
import SnackbarContext from "../../context/SnackbarContext";
import { API_BASE_URL } from '../../config';
import { authHeaders } from '../../utils/api';

const today = () => new Date().toISOString().slice(0, 10);

export default function Profile() {

    const navigate = useNavigate();
    const { authTokens, logoutUser } = useContext(AuthContext);
    const [devices, setDevices] = useState([]);          // browsers that skip the 2FA email
    const [revoking, setRevoking] = useState(false);

    const loadDevices = React.useCallback(async () => {
        try {
            const r = await fetch(`${API_BASE_URL}/api/users/2fa/devices/`, { headers: authHeaders(authTokens) });
            if (r.ok) { const d = await r.json(); setDevices(d.devices || []); }
        } catch { /* leave as is */ }
    }, [authTokens]);

    useEffect(() => { loadDevices(); }, [loadDevices]);

    const onRevokeDevices = async () => {
        setRevoking(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/users/2fa/devices/`, { method: 'DELETE', headers: authHeaders(authTokens) });
            if (!r.ok) throw new Error();
            try { localStorage.removeItem('rawiyaih_device'); } catch { /* ignore */ }
            setDevices([]);
            setSnackbar({ content: 'سيُطلب الرمز مجددًا عند الدخول من كل الأجهزة', success: true });
        } catch {
            setSnackbar({ content: 'تعذر إلغاء الأجهزة الموثوقة', success: false });
        } finally {
            setRevoking(false);
        }
    };
    const { setSnackbar } = useContext(SnackbarContext);
    const [params] = useSearchParams();
    const isWelcome = params.get('welcome') === '1';

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [profile, setProfile] = useState({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        country: '',
        date_of_birth: '',
        bio: '',
        provider: '',
        two_factor_enabled: false,
        is_staff: false,
        notify_on_rating: true,
    });
    const [notifySaving, setNotifySaving] = useState(false);
    const [twoFAPanelOpen, setTwoFAPanelOpen] = useState(false);
    const [twoFAPassword, setTwoFAPassword] = useState('');
    const [twoFASaving, setTwoFASaving] = useState(false);

    useEffect(() => {
        if (!authTokens) {
            navigate('/');
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(`${API_BASE_URL}/api/users/me/`, { headers: authHeaders(authTokens) });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                if (cancelled) return;
                setProfile({
                    username: data.username || '',
                    email: data.email || '',
                    first_name: data.first_name || '',
                    last_name: data.last_name || '',
                    country: data.country || '',
                    date_of_birth: data.date_of_birth || '',
                    bio: data.bio || '',
                    provider: data.provider || '',
                    two_factor_enabled: !!data.two_factor_enabled,
                    is_staff: !!data.is_staff,
                    notify_on_rating: data.notify_on_rating !== false,
                });
            } catch {
                if (!cancelled) setSnackbar({ content: 'تعذر تحميل الملف الشخصي', success: false });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [authTokens, navigate, setSnackbar]);

    const update = (field) => (e) => setProfile(p => ({ ...p, [field]: e.target.value }));
    const setField = (field) => (value) => setProfile(p => ({ ...p, [field]: value }));

    const onToggleTwoFactor = async (e) => {
        e.preventDefault();
        if (!twoFAPassword) {
            setSnackbar({ content: 'أدخل كلمة المرور أولًا', success: false });
            return;
        }
        setTwoFASaving(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/users/2fa/set/`, {
                method: 'POST',
                headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    enabled: !profile.two_factor_enabled,
                    password: twoFAPassword,
                }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                setSnackbar({ content: data?.detail || 'تعذر التحديث', success: false });
                return;
            }
            setProfile(p => ({ ...p, two_factor_enabled: !!data.two_factor_enabled }));
            setTwoFAPassword('');
            setTwoFAPanelOpen(false);
            setSnackbar({
                content: data.two_factor_enabled ? 'تم تفعيل التحقق الثنائي' : 'تم إيقاف التحقق الثنائي',
                success: true,
            });
        } catch {
            setSnackbar({ content: 'تعذر التحديث', success: false });
        } finally {
            setTwoFASaving(false);
        }
    };

    // Saved on its own, immediately: it's a switch, not part of the form.
    const onToggleNotify = async () => {
        const next = !profile.notify_on_rating;
        setNotifySaving(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/users/me/`, {
                method: 'PATCH',
                headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                body: JSON.stringify({ notify_on_rating: next }),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            setProfile(p => ({ ...p, notify_on_rating: next }));
            setSnackbar({ content: next ? 'ستصلك رسالة عند كل تقييم جديد' : 'تم إيقاف إشعارات التقييم', success: true });
        } catch {
            setSnackbar({ content: 'تعذر التحديث', success: false });
        } finally {
            setNotifySaving(false);
        }
    };

    const onSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/users/me/`, {
                method: 'PATCH',
                headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    first_name: profile.first_name,
                    last_name: profile.last_name,
                    country: profile.country,
                    date_of_birth: profile.date_of_birth || null,
                    bio: profile.bio,
                    email: profile.email,
                }),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                const msg = data?.email?.[0] || data?.country?.[0] || data?.date_of_birth?.[0] || 'تعذر الحفظ';
                setSnackbar({ content: msg, success: false });
                return;
            }
            setSnackbar({ content: 'تم حفظ الملف الشخصي', success: true });
            if (isWelcome) navigate('/myworks');
        } catch {
            setSnackbar({ content: 'تعذر الحفظ', success: false });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
        <Navbar />
        <main className='profile-page'>
            <div className='profile-container'>
                <header className='profile-header'>
                    <h1>{isWelcome ? 'أكمل ملفك الشخصي' : 'الملف الشخصي'}</h1>
                    <p>
                        {isWelcome
                            ? 'بعض المعلومات تساعدنا في تخصيص تجربتك على راوية. يمكنك تخطّيها وإكمالها لاحقًا.'
                            : 'حدّث معلوماتك في أي وقت.'}
                    </p>
                </header>

                {loading ? (
                    <p className='profile-empty'>جارٍ التحميل…</p>
                ) : (
                    <form className='profile-form' onSubmit={onSave}>
                        <div className='profile-row'>
                            <label>
                                <span>اسم المستخدم</span>
                                <input type='text' value={profile.username} readOnly className='is-readonly' />
                            </label>
                            <label>
                                <span>البريد الإلكتروني</span>
                                <input
                                    type='email'
                                    value={profile.email}
                                    onChange={update('email')}
                                    readOnly={profile.provider === 'google'}
                                    className={profile.provider === 'google' ? 'is-readonly' : ''}
                                />
                            </label>
                        </div>

                        <div className='profile-row'>
                            <label>
                                <span>الاسم الأول</span>
                                <input type='text' value={profile.first_name} onChange={update('first_name')} />
                            </label>
                            <label>
                                <span>اسم العائلة</span>
                                <input type='text' value={profile.last_name} onChange={update('last_name')} />
                            </label>
                        </div>

                        <div className='profile-row'>
                            <label>
                                <span>الدولة</span>
                                <CountryPicker
                                    value={profile.country}
                                    onChange={setField('country')}
                                />
                            </label>
                            <label>
                                <span>تاريخ الميلاد</span>
                                <input
                                    type='date'
                                    value={profile.date_of_birth || ''}
                                    onChange={update('date_of_birth')}
                                    max={today()}
                                />
                            </label>
                        </div>

                        <label className='profile-full'>
                            <span>نبذة عنك <small>(اختياري — حتى ٥٠٠ حرف)</small></span>
                            <textarea
                                rows={5}
                                maxLength={500}
                                value={profile.bio}
                                onChange={update('bio')}
                                placeholder='اكتب نبذة قصيرة يعرّف بها القرّاء عليك…'
                            />
                        </label>

                        <div className='profile-2fa'>
                            <div className='profile-2fa-header'>
                                <div>
                                    <strong>إشعارات التقييم</strong>
                                    <p>
                                        {profile.notify_on_rating
                                            ? 'مُفعَّلة — تصلك رسالة على بريدك عندما يقيّم أحد القرّاء كتابًا لك.'
                                            : 'موقوفة — لن تصلك رسائل عند تقييم كتبك.'}
                                    </p>
                                </div>
                                <button
                                    type='button'
                                    className='profile-button profile-button--ghost'
                                    onClick={onToggleNotify}
                                    disabled={notifySaving}
                                    aria-pressed={profile.notify_on_rating}
                                >
                                    {notifySaving ? '…' : (profile.notify_on_rating ? 'إيقاف' : 'تفعيل')}
                                </button>
                            </div>
                        </div>

                        {profile.provider !== 'google' && (
                            <div className='profile-2fa'>
                                <div className='profile-2fa-header'>
                                    <div>
                                        <strong>التحقق الثنائي عبر البريد</strong>
                                        <p>
                                            {profile.is_staff
                                                ? 'مُفعَّل إلزاميًا لحسابات الإشراف ولا يمكن إيقافه — يُرسَل رمز لبريدك عند كل تسجيل دخول.'
                                                : profile.two_factor_enabled
                                                    ? 'مُفعَّل — سيُطلب منك رمز يُرسَل لبريدك عند كل تسجيل دخول.'
                                                    : 'موقوف — تسجيل الدخول يتم بكلمة المرور فقط.'}
                                        </p>
                                    </div>
                                    {!profile.is_staff && (
                                        <button
                                            type='button'
                                            className='profile-button profile-button--ghost'
                                            onClick={() => { setTwoFAPanelOpen(o => !o); setTwoFAPassword(''); }}
                                        >
                                            {twoFAPanelOpen
                                                ? 'إلغاء'
                                                : (profile.two_factor_enabled ? 'إيقاف' : 'تفعيل')}
                                        </button>
                                    )}
                                </div>
                                {(profile.two_factor_enabled || profile.is_staff) && (
                                    <div className='profile-2fa-devices'>
                                        {devices.length === 0 ? (
                                            <p>لا توجد أجهزة موثوقة. عند إدخال الرمز يمكنك اختيار ألا يُطلب منك على ذلك الجهاز لمدة ٣٠ يومًا.</p>
                                        ) : (
                                            <>
                                            <p>
                                                أجهزة لا تطلب الرمز حاليًا: {devices.map(d => d.label || 'جهاز').join('، ')}
                                                {' '}({devices.length.toLocaleString('ar-EG')})
                                            </p>
                                            <button type='button' className='profile-button profile-button--ghost'
                                                    onClick={onRevokeDevices} disabled={revoking}>
                                                {revoking ? 'جارٍ الإلغاء…' : 'اطلب الرمز من كل الأجهزة مجددًا'}
                                            </button>
                                            </>
                                        )}
                                    </div>
                                )}
                                {twoFAPanelOpen && !profile.is_staff && (
                                    <div className='profile-2fa-confirm'>
                                        <label>
                                            <span>أكِّد كلمة المرور</span>
                                            <input
                                                type='password'
                                                value={twoFAPassword}
                                                onChange={(e) => setTwoFAPassword(e.target.value)}
                                                autoComplete='current-password'
                                            />
                                        </label>
                                        <button
                                            type='button'
                                            className='profile-button'
                                            onClick={onToggleTwoFactor}
                                            disabled={twoFASaving || !twoFAPassword}
                                        >
                                            {twoFASaving
                                                ? 'جارٍ الحفظ…'
                                                : (profile.two_factor_enabled ? 'تأكيد الإيقاف' : 'تأكيد التفعيل')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className='profile-actions'>
                            {isWelcome && (
                                <button
                                    type='button'
                                    className='profile-button profile-button--ghost'
                                    onClick={() => navigate('/myworks')}
                                >
                                    تخطٍّ الآن
                                </button>
                            )}
                            <button type='submit' className='profile-button' disabled={saving}>
                                {saving ? 'جارٍ الحفظ…' : 'حفظ'}
                            </button>
                        </div>

                        {!isWelcome && (
                            <button
                                type='button'
                                className='profile-logout'
                                onClick={() => { logoutUser(); }}
                            >
                                تسجيل الخروج
                            </button>
                        )}
                    </form>
                )}
            </div>
        </main>
        <Footer />
        </>
    );
}
